import React, { useState, useEffect, useCallback } from 'react';

const TOUR_STEPS = [
  {
    selector: '.tab-bar',
    title: 'Navigation',
    description: 'Switch between Live coaching, Call History, Playbooks, and Settings from here.',
    position: 'bottom',
  },
  {
    selector: '.transcript-panel',
    title: 'Live Transcript',
    description: "Real-time transcription of both sides of the call appears here. You'll see who said what as the conversation unfolds.",
    position: 'right',
  },
  {
    selector: '.coaching-panel',
    title: 'AI Coaching',
    description: 'AI coaching suggestions appear here during calls, analyzing the conversation and providing real-time guidance based on your selected playbook.',
    position: 'left',
  },
  {
    selector: '.playbook-selector',
    title: 'Playbook Selector',
    description: "Choose a coaching playbook to change the AI's focus — Sales, Support, Compliance, or create your own custom playbooks.",
    position: 'left',
  },
  {
    selector: '.privacy-badge',
    title: 'Your Data, Your Privacy',
    description: 'Your data is encrypted and completely private. We never sell or share your information with anyone. Your calls, transcripts, and coaching data belong to you.',
    position: 'left',
  },
  {
    selector: '.dialer-bar',
    title: 'Phone Dialer',
    description: 'Make outbound calls by entering a number, or receive inbound calls directly in your browser. Select your mic and speaker here too.',
    position: 'top',
  },
  {
    selector: '.tab-bar .tab-button:nth-child(2)',
    title: 'Call History',
    description: 'Every coached call generates a detailed report with a performance score, full transcript, and review of all coaching suggestions.',
    position: 'bottom',
  },
  {
    selector: '.tab-bar .tab-button:nth-child(4)',
    title: 'Settings',
    description: 'Configure your phone number, connect your CRM, manage billing, and restart this tour anytime.',
    position: 'bottom',
  },
];

export default function GuidedTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    const el = document.querySelector(TOUR_STEPS[step]?.selector);
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const interval = setInterval(measure, 500);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      clearInterval(interval);
    };
  }, [measure]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onComplete();
      }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
        if (step < TOUR_STEPS.length - 1) setStep((s) => s + 1);
        else onComplete();
      }
      if (e.key === 'ArrowLeft' && step > 0) {
        e.stopPropagation();
        setStep((s) => s - 1);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [step, onComplete]);

  if (!rect) return null;

  const PAD = 8;
  const GAP = 16;
  const current = TOUR_STEPS[step];

  const spotlightStyle = {
    position: 'fixed',
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: 12,
    border: '2px solid rgba(99, 102, 241, 0.5)',
    boxShadow:
      '0 0 0 9999px rgba(0, 0, 0, 0.75), 0 0 30px rgba(99, 102, 241, 0.15)',
    zIndex: 10001,
    pointerEvents: 'none',
    transition:
      'top 0.35s ease, left 0.35s ease, width 0.35s ease, height 0.35s ease',
  };

  const tooltipStyle = { position: 'fixed', zIndex: 10002 };
  const TW = 340;
  const MARGIN = 16;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const clampX = (x) => Math.max(MARGIN, Math.min(vw - TW - MARGIN, x));
  const clampY = (y) => Math.max(MARGIN, Math.min(vh - 220, y));

  switch (current.position) {
    case 'bottom':
      tooltipStyle.top = rect.top + rect.height + PAD + GAP;
      tooltipStyle.left = clampX(rect.left + rect.width / 2 - TW / 2);
      break;
    case 'top':
      tooltipStyle.top = rect.top - PAD - GAP;
      tooltipStyle.left = clampX(rect.left + rect.width / 2 - TW / 2);
      tooltipStyle.transform = 'translateY(-100%)';
      break;
    case 'left':
      tooltipStyle.top = clampY(rect.top + rect.height / 2 - 110);
      tooltipStyle.left = rect.left - PAD - GAP;
      tooltipStyle.transform = 'translateX(-100%)';
      break;
    case 'right':
      tooltipStyle.top = clampY(rect.top + rect.height / 2 - 110);
      tooltipStyle.left = rect.left + rect.width + PAD + GAP;
      break;
    default:
      tooltipStyle.top = rect.top + rect.height + PAD + GAP;
      tooltipStyle.left = clampX(rect.left);
  }

  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  return (
    <>
      <div className="tour-backdrop" onClick={onComplete} />
      <div style={spotlightStyle} />
      <div className="tour-tooltip" key={step} style={tooltipStyle}>
        <div className="tour-tooltip-progress">
          <div
            className="tour-tooltip-progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="tour-tooltip-step">
          Step {step + 1} of {TOUR_STEPS.length}
        </div>
        <div className="tour-tooltip-title">{current.title}</div>
        <div className="tour-tooltip-desc">{current.description}</div>
        <div className="tour-tooltip-nav">
          <button
            className="tour-btn tour-btn-skip"
            onClick={onComplete}
          >
            Skip Tour
          </button>
          <div className="tour-tooltip-nav-right">
            {step > 0 && (
              <button
                className="tour-btn tour-btn-prev"
                onClick={() => setStep(step - 1)}
              >
                Back
              </button>
            )}
            <button
              className="tour-btn tour-btn-next"
              onClick={() => {
                if (step < TOUR_STEPS.length - 1) setStep(step + 1);
                else onComplete();
              }}
            >
              {step === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
